import { Fragment } from "react";
import {
  formatNumber,
  formatDuration,
  formatWeight,
  formatPace,
  formatPercent
} from "../utils/format.js";
import { leadAmong } from "../utils/analytics.js";
import { accentVars } from "../utils/palette.js";
import styles from "./ComparisonKPIGrid.module.css";

function Verdict({ profiles, lead }) {
  if (!lead) {
    return <span className={`${styles.verdict} ${styles.tie}`}>— even</span>;
  }
  // Always the handle: nothing on the card labels a column A or B, so the
  // two-profile shorthand named something the reader could not find.
  const name = `@${profiles[lead.index].username}`;
  const pct = lead.percent != null ? ` · ${Math.abs(lead.percent * 100).toFixed(0)}%` : "";
  return (
    <span
      className={`${styles.verdict} ${styles.win}`}
      style={accentVars(profiles[lead.index].accent)}
      aria-label={`${name} leads${pct ? ` by ${Math.abs(lead.percent * 100).toFixed(0)} percent` : ""}`}
    >
      ▲ {name} leads{pct}
    </span>
  );
}

function MetricCard({ label, cells, lead, profiles, index, pair }) {
  return (
    <div className={styles.card} style={{ "--i": index }}>
      <span className={styles.label}>{label}</span>
      <div className={pair ? styles.duel : styles.stack}>
        {profiles.map((profile, i) => (
          <Fragment key={profile.username}>
            {pair && i > 0 && <div className={styles.divider} aria-hidden="true" />}
            <div
              className={`${styles.side} ${lead ? (lead.index === i ? styles.win : styles.lose) : ""}`}
              style={accentVars(profile.accent)}
            >
              <span className={styles.name}>@{profile.username}</span>
              <strong className={styles.value}>{cells[i].value}</strong>
              {cells[i].sub && <small className={styles.sub}>{cells[i].sub}</small>}
            </div>
          </Fragment>
        ))}
      </div>
      <Verdict profiles={profiles} lead={lead} />
    </div>
  );
}

export default function ComparisonKPIGrid({ profiles, weightUnit, periodDays }) {
  if (!profiles?.length) return null;

  const pair = profiles.length === 2;
  const metrics = profiles.map((profile) => profile.analytics.current);
  const cells = (render) => metrics.map(render);

  const cards = [
    {
      label: "Workouts",
      cells: cells((m) => ({ value: formatNumber(m.workoutCount), sub: formatPace(m.weeklyWorkoutAverage) })),
      lead: leadAmong(metrics.map((m) => m.workoutCount))
    },
    {
      label: "Training time",
      cells: cells((m) => ({
        value: formatDuration(m.totalDurationSeconds),
        sub: `${formatDuration(m.averageDurationSeconds)} avg`
      })),
      lead: leadAmong(metrics.map((m) => m.totalDurationSeconds))
    },
    {
      label: "Volume",
      cells: cells((m) => ({
        value: formatWeight(m.totalVolumeKg, weightUnit),
        sub: `${formatWeight(m.averageVolumeKg, weightUnit)} / workout`
      })),
      lead: leadAmong(metrics.map((m) => m.totalVolumeKg))
    },
    {
      label: "Sets",
      cells: cells((m) => ({
        value: formatNumber(m.totalSets),
        sub: `${formatNumber(Math.round(m.averageSets))} avg / workout`
      })),
      lead: leadAmong(metrics.map((m) => m.totalSets))
    },
    {
      label: "PR sets",
      cells: cells((m) => ({ value: formatNumber(m.prSetCount), sub: `${formatPercent(m.prRate)} of sets` })),
      lead: leadAmong(metrics.map((m) => m.prSetCount))
    }
  ];

  if (metrics.some((m) => m.caloriesWorkoutCount > 0)) {
    const kcal = (v) => (v > 0 ? `${formatNumber(Math.round(v))} kcal` : "—");
    cards.push({
      label: "Calories",
      cells: cells((m) => ({
        value: kcal(m.totalCalories),
        sub: m.caloriesWorkoutCount > 0 ? `${kcal(m.averageCalories)} avg` : null
      })),
      lead: leadAmong(metrics.map((m) => m.totalCalories))
    });
  }

  if (metrics.some((m) => m.heartRateWorkoutCount > 0)) {
    const bpm = (v) => (v > 0 ? `${Math.round(v)} bpm` : "—");
    // Hevy only stores a peak HR for workouts recorded with a heart-rate
    // sensor; fall back to the tracked-workout count when none is present so
    // the sub never reads "— peak".
    const hrSub = (m) => {
      if (m.heartRateWorkoutCount <= 0) return null;
      if (m.maxHeartRate > 0) return `${bpm(m.maxHeartRate)} peak`;
      return `${formatNumber(m.heartRateWorkoutCount)} tracked`;
    };
    cards.push({
      label: "Heart rate",
      cells: cells((m) => ({ value: bpm(m.averageHeartRate), sub: hrSub(m) })),
      lead: leadAmong(metrics.map((m) => m.averageHeartRate))
    });
  }

  return (
    <div className={styles.grid}>
      {cards.map((card, i) => (
        <MetricCard
          key={`${card.label}-${periodDays}`}
          {...card}
          index={i}
          profiles={profiles}
          pair={pair}
        />
      ))}
    </div>
  );
}
